import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { QrCode, User, Calendar, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VisitorPassForm = ({ onSuccess, onCancel }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [createdPass, setCreatedPass] = useState(null);
  const [formData, setFormData] = useState({
    visitor_name: "",
    visitor_phone: "",
    visitor_id_number: "",
    purpose: "",
    host_employee_id: "",
    valid_from: "",
    valid_until: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.visitor_name || !formData.purpose || !formData.host_employee_id || 
        !formData.valid_from || !formData.valid_until) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/visitor-passes`, formData, {
        headers: getAuthHeaders(),
      });
      setCreatedPass(response.data);
      toast.success("Visitor pass created successfully!");
    } catch (error) {
      console.error("Error creating visitor pass:", error);
      toast.error(error.response?.data?.detail || "Failed to create visitor pass");
    } finally {
      setLoading(false);
    }
  };

  if (createdPass) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <QrCode className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading font-bold text-lg text-stone-800">Visitor Pass Created</h3>
          <p className="text-sm text-stone-500">The visitor can photograph this QR code</p>
        </div>

        <div className="bg-stone-50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <User className="w-5 h-5 text-stone-400" />
            <div>
              <p className="font-medium text-stone-800">{createdPass.visitor_name}</p>
              <p className="text-sm text-stone-500">Host: {createdPass.host_name}</p>
            </div>
          </div>
          <div className="text-sm text-stone-500">
            <p>Valid: {createdPass.valid_from} to {createdPass.valid_until}</p>
            <p>Purpose: {createdPass.purpose}</p>
          </div>
        </div>

        {createdPass.qr_code && (
          <div className="flex flex-col items-center p-4">
            <div className="bg-white p-4 rounded-xl shadow-inner border border-stone-200">
              <img
                src={`data:image/png;base64,${createdPass.qr_code}`}
                alt="Visitor Pass QR Code"
                className="w-48 h-48"
                data-testid="visitor-qr-code"
              />
            </div>
            <p className="text-xs text-stone-400 mt-3">
              Visitor can take a picture of this QR code
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button onClick={onSuccess} className="bg-primary hover:bg-primary/90 rounded-xl">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Visitor Name *</Label>
          <Input
            data-testid="visitor-name"
            placeholder="Enter visitor name"
            value={formData.visitor_name}
            onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Phone Number</Label>
          <Input
            data-testid="visitor-phone"
            placeholder="Phone number"
            value={formData.visitor_phone}
            onChange={(e) => setFormData({ ...formData, visitor_phone: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">ID Number</Label>
          <Input
            data-testid="visitor-id"
            placeholder="Passport/ID number"
            value={formData.visitor_id_number}
            onChange={(e) => setFormData({ ...formData, visitor_id_number: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Host Employee ID *</Label>
          <Input
            data-testid="host-employee-id"
            placeholder="e.g., STAFF001"
            value={formData.host_employee_id}
            onChange={(e) => setFormData({ ...formData, host_employee_id: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-semibold">Purpose of Visit *</Label>
        <Textarea
          data-testid="visitor-purpose"
          placeholder="Reason for visit"
          value={formData.purpose}
          onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
          rows={2}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Valid From *</Label>
          <Input
            type="datetime-local"
            data-testid="valid-from"
            value={formData.valid_from}
            onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Valid Until *</Label>
          <Input
            type="datetime-local"
            data-testid="valid-until"
            value={formData.valid_until}
            onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-xl">
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={loading}
          data-testid="create-visitor-pass-btn"
          className="bg-primary hover:bg-primary/90 rounded-xl"
        >
          {loading ? "Creating..." : "Create Pass"}
        </Button>
      </div>
    </form>
  );
};

export default VisitorPassForm;
